import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, BedDoubleIcon, CheckIcon, ClockIcon, PlusIcon, UsersIcon } from 'lucide-react';
import { Property } from '../types/booking';
import { AMENITIES_PLACEHOLDER, stayTimes } from '../data/units';
import { formatNaira } from '../utils/format';
import { Reveal } from './Reveal';
interface UnitShowcaseProps {
  unit: Property;
  reversed?: boolean;
}
export function UnitShowcase({
  unit,
  reversed = false
}: UnitShowcaseProps) {
  const [activeImage, setActiveImage] = useState(0);
  const image = unit.images[activeImage];
  return <Reveal as="article" className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-365" data-mp="3ivmwn9n3zgi8">
      <div className={reversed ? 'lg:order-2' : ''} data-id="element-366" data-mp="3nsvgrfrthum4">
        <div className="relative overflow-hidden rounded-sm bg-forest-100 shadow-soft" data-id="element-367" data-mp="1h1w24r98com0">
          <AnimatePresence mode="wait" initial={false} data-id="element-368" data-mp="1m1s8gx5egs4q">
            <motion.img key={image.src} src={image.src} alt={image.alt} loading="lazy" decoding="async" initial={{
            opacity: 0,
            scale: 1.02
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} className="aspect-[4/3] w-full object-cover" data-id="element-369" data-mp="1lz60wxiiecmi" />
          </AnimatePresence>
          <span className="absolute left-4 top-4 rounded-full bg-forest-700/85 px-3 py-1 text-[11px] tracking-wide text-ivory-100" data-id="element-370" data-mp="28wropjng3xvy">
            ID {unit.id}
          </span>
        </div>
        <div className="mt-3 flex gap-3" role="tablist" aria-label={`${unit.name} photos`} data-id="element-371" data-mp="2hxplphzgv551">
          {unit.images.map((item, index) => <button key={item.src} type="button" role="tab" aria-selected={index === activeImage} aria-label={item.alt} onClick={() => setActiveImage(index)} className={`overflow-hidden rounded-sm transition-opacity duration-150 ease-premium ${index === activeImage ? 'opacity-100 ring-1 ring-gold-400' : 'opacity-60 hover:opacity-100'}`} data-id="element-372" data-mp="2d065icj7kiag" data-mpi={index}>
              <img src={item.src} alt="" aria-hidden="true" loading="lazy" className="h-16 w-24 object-cover" data-id="element-373" data-mp="2uy5yihykrr8i" />
            </button>)}
        </div>
      </div>

      <div className={reversed ? 'lg:order-1' : ''} data-id="element-374" data-mp="23adx8zsb7klx">
        <p className="text-[11px] uppercase tracking-wordmark text-gold-700" data-id="element-375" data-mp="27ao38cg3qb0s">Apartment stay</p>
        <h3 className="mt-3 font-display text-3xl leading-tight text-forest-700 sm:text-[2.4rem]" data-id="element-376" data-mp="2c9r91z3wol9t">{unit.name}</h3>
        <p className="mt-3 text-base text-gold-700" data-id="element-377" data-mp="2h07o1nndkeya">{unit.tagline}</p>
        <p className="mt-5 text-[15px] leading-relaxed text-muted" data-id="element-378" data-mp="2h2kdsmzdjtlw">{unit.description}</p>

        <dl className="mt-7 grid grid-cols-3 gap-6 border-y border-forest-700/10 py-5" data-id="element-379" data-mp="1jktti3r2x2kr">
          <Spec icon={<BedDoubleIcon size={17} data-id="element-381" data-mp="1oi3odyx8m6fz" />} label="Bedrooms" value={`${unit.bedrooms}`} data-id="element-380" data-mp="1oilqhc6kzevt" />
          <Spec icon={<UsersIcon size={17} data-id="element-383" data-mp="1gzvzt1vju8lu" />} label="Max guests" value={`${unit.maxGuests}`} data-id="element-382" data-mp="1h05kc9xtrnk5" />
          <Spec icon={<ClockIcon size={17} data-id="element-385" data-mp="1c2dpb71vvlbq" />} label="In / out" value={`2 PM / 12 PM`} data-id="element-384" data-mp="1c240vnwhlm85" />
        </dl>

        <ul className="mt-6 grid gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-386" data-mp="170wsbmmisj0f">
          {unit.amenities.map(amenity => <li key={amenity} className="flex items-start gap-2.5 text-sm text-ink" data-id="element-387" data-mp="132z34jsr1wx1">
              <CheckIcon size={15} className="mt-0.5 shrink-0 text-gold-500" aria-hidden="true" data-id="element-388" data-mp="y34b2cx8n1ae" />
              {amenity}
            </li>)}
          <li className="flex items-start gap-2.5 text-sm italic text-muted" data-id="element-389" data-mp="obgpgsnd4egu">
            <PlusIcon size={15} className="mt-0.5 shrink-0 text-forest-300" aria-hidden="true" data-id="element-390" data-mp="jcx2qq0y2ynf" />
            {AMENITIES_PLACEHOLDER}
          </li>
        </ul>

        <div className="mt-8 flex flex-wrap items-end justify-between gap-5" data-id="element-391" data-mp="fasbtz1vsg4h">
          <p className="font-display text-3xl text-forest-700" data-id="element-392" data-mp="ad9ylwgtzeqo">
            {formatNaira(unit.nightlyRate)}
            <span className="ml-2 font-sans text-xs uppercase tracking-overline text-gold-700" data-id="element-393" data-mp="2afrolkv6r2p1">per night</span>
          </p>
          <Link to={`/booking?property=${unit.id}`} className="group inline-flex items-center gap-2 rounded-full bg-forest-700 px-6 py-3 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-forest-600" data-id="element-394" data-mp="2efa6e7rpmxnn">
            Book now
            <ArrowRightIcon size={15} className="transition-transform duration-200 ease-premium group-hover:translate-x-1" aria-hidden="true" data-id="element-395" data-mp="1qoxrue1ym2o3" />
          </Link>
        </div>
        <p className="mt-3 text-[12px] text-muted" data-id="element-396" data-mp="1hrvr4dxsl6r7">
          Check-in {stayTimes.checkIn} · Checkout {stayTimes.checkOut}
        </p>
      </div>
    </Reveal>;
}
function Spec({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div data-id="element-397" data-mp="3h2et5u90xk1i">
      <dt className="flex items-center gap-2 text-[10px] uppercase tracking-overline text-gold-700" data-id="element-398" data-mp="3h25cix9vcp8h">
        <span className="text-gold-500" aria-hidden="true" data-id="element-399" data-mp="3r73o8m63mijo">
          {icon}
        </span>
        {label}
      </dt>
      <dd className="mt-1.5 text-[15px] text-ink" data-id="element-400" data-mp="3m5xr8c5eo94c">{value}</dd>
    </div>;
}